import type { ElementType } from "react"

import { FEATURE_FLAGS } from "@/feature-flags"
import {
  Activity,
  BookA,
  Building2,
  ChartNoAxesCombined,
  CircleUserRound,
  CreditCard,
  Gauge,
  HeartPulse,
  KeyRound,
  LayoutGrid,
  ListFilter,
  Palette,
  Puzzle,
  Sparkles,
  Tags,
  Users,
  Webhook,
} from "lucide-react"

const SETTINGS_BASE_PATH = "/settings"

export type SettingsSectionId =
  | "organization"
  | "sofia"
  | "profile"
  | "appearance"
  | "dictionary"
  | "user-management"
  | "workspaces"
  | "tags"
  | "default-metrics"
  | "conditional-metrics"
  | "api-keys"
  | "webhooks"
  | "integrations"
  | "system-status"
  | "health"
  | "billing"
  | "usage"

type SettingsGroupId =
  | "appearance"
  | "account"
  | "organization"
  | "workspaces"
  | "health"
  | "billing"

export type SettingsSectionFlag =
  | typeof FEATURE_FLAGS.USER_MANAGEMENT
  | typeof FEATURE_FLAGS.UPLOADED_AUDIO_CHANNEL_SELECTION

interface SettingsSection {
  id: SettingsSectionId
  group: SettingsGroupId
  label: string
  /** Name used outside its sidebar group, where General would be ambiguous. */
  title?: string
  description: string
  icon: ElementType
  featureFlag?: SettingsSectionFlag
  anyFeatureFlags?: readonly SettingsSectionFlag[]
  path?: string
  scope?: "workspace"
}

interface SettingsGroup {
  id: SettingsGroupId
  label: string
}

const SETTINGS_GROUPS: readonly SettingsGroup[] = [
  { id: "account", label: "Account" },
  { id: "appearance", label: "Appearance" },
  { id: "organization", label: "Organization" },
  { id: "workspaces", label: "Workspaces" },
  { id: "health", label: "Health" },
  { id: "billing", label: "Billing" },
]

export const SETTINGS_SECTIONS: readonly SettingsSection[] = [
  {
    id: "profile",
    title: "Account",
    icon: CircleUserRound,
    group: "account",
    label: "General",
    path: getAccountSettingsPath("profile"),
    description: "Your personal details, sign-in security, and account access.",
  },
  {
    id: "appearance",
    icon: Palette,
    group: "appearance",
    label: "Theme",
    description: "How Coval looks on this device.",
  },
  {
    id: "organization",
    title: "Organization",
    icon: Building2,
    group: "organization",
    label: "General",
    description: "Your organization’s settings.",
    anyFeatureFlags: [
      FEATURE_FLAGS.USER_MANAGEMENT,
      FEATURE_FLAGS.UPLOADED_AUDIO_CHANNEL_SELECTION,
    ],
  },
  {
    id: "user-management",
    icon: Users,
    group: "organization",
    label: "Members",
    path: "/settings/organization/members",
    description: "Members, organization roles, and pending invitations.",
    featureFlag: FEATURE_FLAGS.USER_MANAGEMENT,
  },
  {
    id: "sofia",
    icon: Sparkles,
    group: "organization",
    label: "Sofia",
    title: "Sofia Settings",
    description: "Manage Sofia’s preferences for your organization.",
  },
  {
    id: "dictionary",
    icon: BookA,
    group: "organization",
    label: "Dictionary",
    description:
      "Words and phrases transcription should recognize, so transcripts spell them the way you do.",
  },
  {
    id: "workspaces",
    icon: LayoutGrid,
    group: "workspaces",
    label: "Workspaces",
    description: "Workspaces within this organization.",
  },
  {
    id: "tags",
    icon: Tags,
    group: "workspaces",
    label: "Tags",
    description: "Shared tags for resources in this workspace.",
    path: "/settings/workspaces/tags",
    scope: "workspace",
  },
  {
    id: "default-metrics",
    icon: ChartNoAxesCombined,
    group: "workspaces",
    label: "Default Metrics",
    description:
      "Metrics automatically added to every uploaded conversation in this workspace.",
    path: "/settings/workspaces/metrics/default",
    scope: "workspace",
  },
  {
    id: "conditional-metrics",
    icon: ListFilter,
    group: "workspaces",
    label: "Conditional Metrics",
    description:
      "Metrics added when uploaded conversation metadata matches a rule.",
    path: "/settings/workspaces/metrics/conditional",
    scope: "workspace",
  },
  {
    id: "api-keys",
    icon: KeyRound,
    group: "organization",
    label: "API keys",
    description: "Keys that authenticate programmatic access to Coval.",
  },
  {
    id: "webhooks",
    icon: Webhook,
    group: "organization",
    label: "Webhooks",
    description:
      "Endpoints Coval calls when events happen in this organization.",
  },
  {
    id: "integrations",
    icon: Puzzle,
    group: "organization",
    label: "Integrations",
    description: "Connect Coval to the tools your team already uses.",
  },
  {
    id: "system-status",
    icon: HeartPulse,
    group: "health",
    label: "System Status",
    description: "Current service availability and recent incidents.",
  },
  {
    id: "health",
    icon: Activity,
    group: "health",
    label: "Processing",
    description:
      "Expected start times and current waiting work for this organization.",
  },
  {
    id: "billing",
    icon: CreditCard,
    group: "billing",
    label: "Plan",
    description: "Your subscription and payment details.",
  },
  {
    id: "usage",
    icon: Gauge,
    group: "billing",
    label: "Usage",
    description:
      "Consumption against your plan's limits for the current period.",
  },
]

const SECTIONS_BY_ID = new Map<SettingsSectionId, SettingsSection>(
  SETTINGS_SECTIONS.map((section) => [section.id, section]),
)

/**
 * Maps each tab value the retired tabbed settings page used onto its section,
 * so bookmarked `?tab=` URLs still land somewhere sensible.
 */
const SECTION_BY_LEGACY_TAB: Record<string, SettingsSectionId> = {
  "api-keys": "api-keys",
  billing: "billing",
  integrations: "integrations",
  webhooks: "webhooks",
  workspaces: "workspaces",
  organization: "dictionary",
  account: "profile",
  health: "health",
}

export function getSettingsSection(
  sectionId: SettingsSectionId,
): SettingsSection {
  const section = SECTIONS_BY_ID.get(sectionId)
  if (!section) {
    throw new Error(`Unknown settings section: ${sectionId}`)
  }
  return section
}

export function getSettingsSectionPath(sectionId: SettingsSectionId): string {
  const section = getSettingsSection(sectionId)
  return section.path ?? `${SETTINGS_BASE_PATH}/${sectionId}`
}

export function isWorkspaceSettingsSection(
  sectionId: SettingsSectionId,
): boolean {
  return getSettingsSection(sectionId).scope === "workspace"
}

/**
 * The sections a viewer can reach, in nav order, with flag-gated sections
 * dropped when their flag is off.
 */
export function getSettingsSectionAvailability(
  sectionId: SettingsSectionId,
  featureFlagStatus: Partial<Record<SettingsSectionFlag, boolean>>,
): boolean | undefined {
  const section = getSettingsSection(sectionId)
  const flags =
    section.anyFeatureFlags ??
    (section.featureFlag ? [section.featureFlag] : [])
  if (
    flags.length === 0 ||
    flags.some((flag) => featureFlagStatus[flag] === true)
  ) {
    return true
  }
  if (flags.some((flag) => featureFlagStatus[flag] === undefined)) {
    return undefined
  }
  return false
}

export function getVisibleSettingsSections(
  featureFlagStatus: Partial<Record<SettingsSectionFlag, boolean>>,
): SettingsSection[] {
  return SETTINGS_SECTIONS.filter(
    (section) =>
      getSettingsSectionAvailability(section.id, featureFlagStatus) === true,
  )
}

/**
 * The sections a viewer can reach, in nav order, under the group each belongs
 * to, with empty groups dropped. The nav and the section list read the same
 * shape so the two cannot present settings differently.
 */
export function getVisibleSettingsSectionGroups(
  featureFlagStatus: Partial<Record<SettingsSectionFlag, boolean>>,
): (SettingsGroup & { sections: SettingsSection[] })[] {
  const visible = getVisibleSettingsSections(featureFlagStatus)
  return SETTINGS_GROUPS.map((group) => ({
    ...group,
    sections: visible.filter((section) => section.group === group.id),
  })).filter((group) => group.sections.length > 0)
}

/**
 * The section `/settings` lands on: the first one in the nav that the viewer
 * can actually reach.
 */
export function getDefaultSettingsSectionId(
  featureFlagStatus: Partial<Record<SettingsSectionFlag, boolean>> = {},
): SettingsSectionId {
  const [firstVisible] = getVisibleSettingsSections(featureFlagStatus)
  return (firstVisible ?? SETTINGS_SECTIONS[0]).id
}

/**
 * Resolves the section a legacy `?tab=` value points at, falling back to the
 * landing section for unknown or absent values.
 */
export function getSectionFromLegacyTab(
  tab: string | string[] | undefined,
  featureFlagStatus: Partial<Record<SettingsSectionFlag, boolean>> = {},
): SettingsSectionId {
  const value = Array.isArray(tab) ? tab[0] : tab
  return (
    (value ? SECTION_BY_LEGACY_TAB[value] : undefined) ??
    getDefaultSettingsSectionId(featureFlagStatus)
  )
}

export type AccountSettingsView = "profile" | "security"

export function getAccountSettingsPath(view: AccountSettingsView): string {
  return `/settings/account/${view}`
}
